import { useCallback } from 'react';

import { ApiCharts } from 'api';
import { ICexChartsPoint } from 'api/apiCharts/models';
import { CexStrategy } from 'api/cex';
import { useTypedSelector } from 'store';
import { addBignumbers, bn, humanizeBn, multiplyBignumbers } from 'tools';
import { IAccountBalances, ICexAccount } from 'types/accounts';
import { IChartTimeframe, KLine, Trade } from 'types/charts';
import { chartTimeframeToInterval } from 'utils/charts';
import { ECexName } from 'web3';

export type IAccountWithBalance = ICexAccount & Partial<IAccountBalances>;

const getBalancesFromPrice = (price: string, accounts: IAccountWithBalance[]) => {
  return accounts.reduce(
    (acc, val) => {
      const quoteBN = addBignumbers([bn(acc.quote), 18], [bn(val.quote_balance), 18]);
      const baseBN = addBignumbers([bn(acc.base), 18], [bn(val.base_balance), 18]);

      const quote = humanizeBn(quoteBN);
      const base = humanizeBn(baseBN);

      const quote_usd_bn = quoteBN;
      const base_usd_bn = multiplyBignumbers([baseBN, 18], [bn(price), 18]);

      const total_in_usd = humanizeBn(addBignumbers([quote_usd_bn, 18], [base_usd_bn, 18]));

      return {
        quote,
        quote_usd: humanizeBn(quote_usd_bn),
        base,
        base_usd: humanizeBn(base_usd_bn),
        total_in_usd,
      };
    },
    { quote: '0', base: '0', quote_usd: '0', base_usd: '0', total_in_usd: '0' } as {
      quote: string;
      base: string;
      quote_usd: string;
      base_usd: string;
      total_in_usd: string;
    },
  );
};

export const buildMexcFallbackPoints = (
  klines: KLine[],
  accounts: IAccountWithBalance[],
): ICexChartsPoint[] => {
  if (!klines.length) {
    return [];
  }

  const sortedKlines = [...klines].sort((a, b) => a[0] - b[0]);

  return sortedKlines.map(kline => {
    const [timestamp, open, high, low, close, volume] = kline;
    const balances = getBalancesFromPrice(String(close), accounts);

    return {
      timestamp,
      kline: {
        open: String(open),
        high: String(high),
        low: String(low),
        close: String(close),
        volume: String(volume),
        our_volume: '0',
      },
      balance: balances,
    };
  });
};

const applyBalancesFromSource = (
  targetPoints: ICexChartsPoint[],
  sourcePoints: ICexChartsPoint[],
): ICexChartsPoint[] => {
  if (!targetPoints.length || !sourcePoints.length) {
    return targetPoints;
  }

  const sortedSource = [...sourcePoints].sort((a, b) => a.timestamp - b.timestamp);
  let idx = 0;

  return targetPoints.map(point => {
    while (idx + 1 < sortedSource.length && sortedSource[idx + 1].timestamp <= point.timestamp) {
      idx += 1;
    }

    const sourceBalance = sortedSource[idx]?.balance;
    if (!sourceBalance) {
      return point;
    }

    return {
      ...point,
      balance: sourceBalance,
    };
  });
};

const useLoadCexCharts = () => {
  const cexPair = useTypedSelector(store => store.pairs.selectedCexPair)!;

  const load = useCallback(
    async ({
      startTime,
      endTime,
      timeframe,
      accounts,
      interval,
    }: {
      startTime: number;
      endTime: number;
      timeframe: IChartTimeframe;
      accounts: IAccountWithBalance[];
      interval: number;
    }) => {
      if (!cexPair) return { points: [], usedFallback: false };

      const cexStrategy = CexStrategy({
        cex: cexPair.cex,
        base: cexPair.token_base.symbol,
        quote: cexPair.token_quote.symbol,
        orderbookDepth: 20,
        onOrderbookUpdate: () => {},
        onTickerUpdate: () => {},
        klineUpdate: () => {},
        precisionUpdate: () => {},
        onTradeUpdate: () => {},
      });

      const { isSuccess, data } = await ApiCharts.getCexCharts({
        symbol: `${cexPair.token_base.symbol}-${cexPair.token_quote.symbol}`,
        cex: [cexPair.cex],
        period: timeframe,
        from: startTime,
        to: endTime,
        pair_id: [cexPair.id],
      });

      const trades: Trade[] = [];

      const pointsFromApi: ICexChartsPoint[] = data?.points ?? [];
      const hasMetricsPoints = isSuccess && pointsFromApi.length > 0;
      // These exchanges expose CORS-enabled public candles. Keep other exchanges on
      // market-subscriber history so a slow REST fallback does not regress their chart loads.
      const canUseFallback =
        !hasMetricsPoints &&
        [ECexName.mexc, ECexName.coinbase, ECexName.dextrade, ECexName.gemini].includes(
          cexPair.cex,
        ) &&
        cexStrategy;

      if (canUseFallback) {
        const resolvedInterval = interval || chartTimeframeToInterval(timeframe);
        const fallbackLimit = Math.min(
          1000,
          Math.max(1, Math.ceil((endTime - startTime) / resolvedInterval) + 1),
        );
        const klines = await cexStrategy.fetchKlines({
          base: cexPair.token_base.symbol,
          quote: cexPair.token_quote.symbol,
          limit: fallbackLimit,
          interval: timeframe,
          from: startTime,
          to: endTime,
        });

        let fallbackPoints = buildMexcFallbackPoints(klines ?? [], accounts);
        if (fallbackPoints.length) {
          fallbackPoints.pop();
        }

        try {
          const { isSuccess: balanceSuccess, data: balanceData } = await ApiCharts.getCexCharts({
            symbol: `${cexPair.token_base.symbol}-${cexPair.token_quote.symbol}`,
            cex: [cexPair.cex],
            period: '1h',
            from: startTime,
            to: endTime,
            pair_id: [cexPair.id],
          });

          if (balanceSuccess && balanceData?.points?.length) {
            fallbackPoints = applyBalancesFromSource(fallbackPoints, balanceData.points);
          }
        } catch (error) {
          console.warn('[useLoadCexCharts] balance fallback error', error);
        }

        return {
          points: fallbackPoints,
          trades,
          usedFallback: true,
        };
      }

      // Render stored history as soon as it arrives. The independent market
      // socket updates the current candle; a slow public proxy must not hold
      // confirmed history behind an optional one-candle request.
      return {
        points: hasMetricsPoints ? pointsFromApi : [],
        trades,
        usedFallback: false,
      };
    },
    [cexPair],
  );

  return load;
};

export { useLoadCexCharts };
